import { useCallback } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import { ResponseNotifications, Notification } from '@/api/types';

import api from '@/api';
import { queryKeys } from '@/api/queryKeys';

import { GroupedNotifications, groupNotificationsByDate } from './helpers';

export interface UseNotificationsReturnType {
  groupedNotifications: GroupedNotifications[];
  isLoading: boolean;
  loadMore: () => void;
  hasMore: boolean;
}

export const useNotifications = (): UseNotificationsReturnType => {
  const { data, isLoading, fetchNextPage, hasNextPage } = useInfiniteQuery<ResponseNotifications>({
    queryKey: queryKeys.notifications.list(),
    queryFn: ({ pageParam }) => api.getNotifications(pageParam as number),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = lastPage.meta;
      if (current_page && last_page && current_page < last_page) {
        return current_page + 1;
      }
      return undefined;
    },
  });

  const allNotifications: Notification[] = data?.pages.flatMap((page) => page.data) ?? [];
  const groupedNotifications: GroupedNotifications[] = groupNotificationsByDate(allNotifications);

  const loadMore = useCallback(() => {
    if (!isLoading && hasNextPage) {
      fetchNextPage();
    }
  }, [isLoading, hasNextPage, fetchNextPage]);

  return {
    groupedNotifications,
    isLoading,
    loadMore,
    hasMore: !!hasNextPage,
  };
};
